import React, { useState, useRef } from "react";
import { ErrorLbl, Input, InputContainer, InputLbl } from "./styles";
import { cpf, cnpj } from "cpf-cnpj-validator";
import { TMask } from "./types";

interface IMaskInputLabel {
  label: string;
  name?: string;
  setValue: (value: string) => void;
  type?: TMask;
  required?: boolean;
  setIsValid: (value: boolean) => void;
  isValid: boolean;
}

export const MaskInputLabel: React.FC<IMaskInputLabel> = ({
  label,
  setValue,
  type = "CPF",
  name,
  required = false,
  setIsValid,
  isValid
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [maskedValue, setMaskedValue] = useState("");

  const maskFormats: Record<TMask, string> = {
    CPF: "___.___.___-__",
    CNPJ: "__.___.___/____-__",
    PHONE: "(__) _____-____",
    CEP: "_____-___"
  };

  const applyGenericMask = (value: string, mask: string) => {
    const digits = value.replace(/\D/g, "");
    let result = "";
    let digitIndex = 0;
    for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
      result += mask[i] === "_" ? digits[digitIndex++] : mask[i];
    }
    return result;
  };

  const validateInput = (input: string) => {
    const numericInput = input.replace(/\D/g, "");
    switch (type) {
      case "CNPJ": {
        const result = cnpj.isValid(numericInput) && numericInput.length === 14;
        setIsValid(result);
        return result;
      }
      case "CPF": {
        const result = cpf.isValid(numericInput) && numericInput.length === 11;
        setIsValid(result);
        return result;
      }
      case "PHONE": {
        const result = numericInput.length === 11;
        setIsValid(result);
        return result;
      }
      case "CEP": {
        const result = numericInput.length === 8;
        setIsValid(result);
        return result;
      }
      default:
        return false;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    const newMaskedValue = applyGenericMask(inputVal, maskFormats[type]);
    setMaskedValue(newMaskedValue);
    setValue(newMaskedValue);
    if (newMaskedValue.length >= 10) {
      validateInput(newMaskedValue);
    } else {
      setIsValid(false);
    }
  };

  const handleFocus = () => {
    if (inputRef.current) {
      const mask = maskFormats[type];
      const firstEditableIndex = mask.indexOf("_");
      const position =
        maskedValue === "" ? firstEditableIndex : maskedValue.length;
      setTimeout(() => {
        inputRef.current!.setSelectionRange(position, position);
      }, 0);
    }
  };

  const displayError = () => {
    const numericValue = maskedValue.replace(/\D/g, "");
    switch (type) {
      case "CNPJ":
        return !isValid && numericValue.length === 14;
      case "CPF":
        return !isValid && numericValue.length === 11;
      case "PHONE":
        return !isValid && numericValue.length === 11;
      default:
        return false;
    }
  };

  return (
    <InputLbl>
      <span className="label">{label}</span>
      <InputContainer>
        <Input
          ref={inputRef}
          type="text"
          name={name}
          value={maskedValue}
          onChange={handleChange}
          onFocus={handleFocus}
          placeholder={maskFormats[type]}
          required={required}
          autoComplete="off"
          maxLength={maskFormats[type].length}
        />
      </InputContainer>
      {displayError() && <ErrorLbl>Inválido.</ErrorLbl>}
    </InputLbl>
  );
};
